import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

class NetworkManager {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          const url = (error.config?.url ?? '') as string;
          // Don't redirect on auth endpoints — the caller handles these errors
          const isAuthEndpoint = url.startsWith('/api/auth/');
          if (!isAuthEndpoint) {
            this.token = null;
            if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
              window.location.href = '/login?expired=true';
            }
          }
        }
        return Promise.reject(error);
      },
    );
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    return this.token;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }
}

export const networkManager = new NetworkManager();

/**
 * Extract a human-readable error message from any API error.
 * Works with Axios errors, generic Errors, and unknown types.
 */
export function getErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (!err) return fallback;

  // Axios error with API response body
  const axiosData = (err as { response?: { data?: { message?: string; error?: string; details?: Record<string, string> } } })?.response?.data;
  if (axiosData) {
    // If the backend returned field-level validation details, format them
    if (axiosData.details && typeof axiosData.details === 'object') {
      const detailMessages = Object.entries(axiosData.details)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join('; ');
      if (detailMessages) return detailMessages;
    }
    if (axiosData.message) return axiosData.message;
    if (axiosData.error) return axiosData.error;
  }

  // Axios error with HTTP status text
  const status = (err as { response?: { status?: number; statusText?: string } })?.response;
  if (status?.status === 403) return 'You don\'t have permission to do this';
  if (status?.status === 404) return 'Not found';
  if (status?.status === 409) return 'Conflict — this may already exist';
  if (status?.status === 429) return 'Too many requests — please wait and try again';
  if (status?.status && status.status >= 500) return 'Server error — please try again later';

  // Network error (no response)
  if ((err as { code?: string })?.code === 'ERR_NETWORK') return 'Network error — check your connection';
  if ((err as { code?: string })?.code === 'ECONNABORTED') return 'Request timed out — please try again';

  // Plain Error object
  if (err instanceof Error) return err.message;

  return fallback;
}
